// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  MetricDivider,
  MetricRow,
  PrimaryMetric,
  SummaryCard,
} from '@/components/summary-card';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { SoldAssetCurrencyStats } from '@/report/api/get-sold-assets-snapshot-report';
import { formatNumber } from '@/utilities/number';
import { Grid } from '@mui/material';

interface Props {
  stats: SoldAssetCurrencyStats;
  currencyCode: IsoCurrencyCode;
  isLoading: boolean;
}

/**
 * Renders the sold-asset snapshot summary cards for a single cost-currency
 * bucket. Stats are computed by the backend per currency, so no total here ever
 * mixes currencies; amounts are formatted in the bucket's isoCode.
 */
export function SoldAssetSummaryCards(props: Props) {
  // A null total means no asset reported a gain or loss, so it stays uncolored.
  let gainLossColor: string | undefined;
  if (props.stats.totalGainLossOnSale !== null) {
    gainLossColor =
      Number(props.stats.totalGainLossOnSale) >= 0
        ? 'success.main'
        : 'error.main';
  }

  return (
    <Grid container spacing={3}>
      {/* Asset Overview Card */}
      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Total sold assets"
            value={formatNumber(props.stats.assetCount)}
            isLoading={props.isLoading}
          />
          <MetricDivider>
            <MetricRow
              label="Complete records"
              value={formatNumber(props.stats.assetsWithCompleteAccounting)}
              color="success.main"
              isLoading={props.isLoading}
            />
            <MetricRow
              label="Incomplete records"
              value={formatNumber(props.stats.assetsWithIncompleteAccounting)}
              color="error.main"
              isLoading={props.isLoading}
            />
          </MetricDivider>
        </SummaryCard>
      </Grid>

      {/* Financial Performance Card */}
      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Total sale value"
            value={formatAmountWithCurrency(
              props.stats.totalSaleValue,
              props.currencyCode,
              { sparse: true }
            )}
            isLoading={props.isLoading}
          />
          <MetricDivider>
            <MetricRow
              label="Book value at sale"
              value={formatAmountWithCurrency(
                props.stats.totalBookValueAtSale,
                props.currencyCode,
                { sparse: true }
              )}
              isLoading={props.isLoading}
            />
            <MetricRow
              label="Gain (loss) on sale"
              value={formatAmountWithCurrency(
                props.stats.totalGainLossOnSale,
                props.currencyCode,
                { sparse: true }
              )}
              color={gainLossColor}
              isLoading={props.isLoading}
            />
          </MetricDivider>
        </SummaryCard>
      </Grid>

      {/* Performance Metrics Card */}
      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Avg. inventory days"
            value={formatNumber(props.stats.weightedInventoryDays, {
              placeholder: '—',
            })}
            isLoading={props.isLoading}
            tooltip="Weighted average number of days in inventory (dollar-weighted by asset cost basis)"
          />
          <MetricDivider>
            <MetricRow
              label="Avg. in-service days (on our books)"
              value={formatNumber(props.stats.weightedServiceDaysOnBooks, {
                placeholder: '—',
              })}
              isLoading={props.isLoading}
              tooltip="Weighted average in-service days ON our books before asset sale (dollar-weighted by asset cost basis)"
            />
            <MetricRow
              label="Avg. in-service days (off our books)"
              value={formatNumber(props.stats.weightedServiceDaysOffBooks, {
                placeholder: '—',
              })}
              isLoading={props.isLoading}
              tooltip="Weighted average service days OFF our books after asset sale (dollar-weighted by asset cost basis)"
            />
          </MetricDivider>
        </SummaryCard>
      </Grid>
    </Grid>
  );
}
